// Manufacturers' digital service books (sähköinen huoltokirja) that an independent workshop in
// Finland can write a service entry into, keyed by make. No manufacturer offers an API for
// writing entries, so the work order only links to the portal and the workshop records the
// service there, logged in with its own account.
//
// Which makes are listed: OE Service's per-country list (oeservice.eu/en/manufacturers, flag
// is_original_dsb_available for FI), plus Hyundai's Finnish importer portal for independent
// workshops, checked 2026-10. Volvo, Kia, Stellantis, Renault/Dacia, Nissan, Honda and Tesla
// are deliberately absent: their service books are closed to independents in Finland (Honda's
// portal does not take Finnish workshops, Tesla's entries are made in the car), and the work
// order shows no button for them. URLs were checked by hand on the same date; update this list
// when a portal moves (erWin already moved once, from erwin.<brand>.* to <brand>.erwin-store.com).

const erwin = (brand) => `https://${brand}.erwin-store.com/erwin/showHome.do`;

export const SERVICE_BOOK_PORTALS = {
  erwinVolkswagen: { name: 'erWin Volkswagen', url: erwin('volkswagen') },
  erwinAudi: { name: 'erWin Audi', url: erwin('audi') },
  erwinSkoda: { name: 'erWin Škoda', url: erwin('skoda') },
  erwinSeat: { name: 'erWin SEAT / CUPRA', url: erwin('seat') },
  mercedes: { name: 'Mercedes-Benz B2B Connect', url: 'https://b2bconnect.mercedes-benz.com/fi' },
  bmw: { name: 'BMW Group AOS', url: 'https://aos.bmwgroup.com/web/oss/start' },
  ford: { name: 'Ford Service Info', url: 'https://www.fordserviceinfo.com/' },
  hyundai: { name: 'Hyundai Huoltokirja', url: 'https://huoltokirja.hyundai.fi/' },
  mazda: { name: 'Mazda Europe Portal', url: 'https://portal.mazdaeur.com/' },
  toyota: { name: 'Toyota Tech', url: 'https://www.toyota-tech.eu/' },
  lexus: { name: 'Lexus Tech', url: 'https://www.lexus-tech.eu/' },
  mg: { name: 'MG RMI', url: 'https://rmi.mgmotor.eu/' },
  jlr: { name: 'JLR TOPIx', url: 'https://topix.jlrext.com/' },
};

// Keys are makes after normalizeMake(), i.e. the first word only ("LAND ROVER" -> "LAND").
const MAKE_TO_PORTAL = {
  VOLKSWAGEN: 'erwinVolkswagen',
  AUDI: 'erwinAudi',
  SKODA: 'erwinSkoda',
  SEAT: 'erwinSeat',
  CUPRA: 'erwinSeat', // CUPRA cars live in SEAT's erWin
  MERCEDES: 'mercedes',
  SMART: 'mercedes',
  BMW: 'bmw',
  MINI: 'bmw',
  ROLLS: 'bmw',
  FORD: 'ford',
  HYUNDAI: 'hyundai',
  MAZDA: 'mazda',
  TOYOTA: 'toyota',
  LEXUS: 'lexus',
  MG: 'mg',
  JAGUAR: 'jlr',
  LAND: 'jlr',
  RANGE: 'jlr',
};

// Same spellings the backend folds in composition/services/enrichment.py _MAKE_ALIASES.
const MAKE_ALIASES = {
  VW: 'VOLKSWAGEN',
  MB: 'MERCEDES',
  MERSEDES: 'MERCEDES',
  MERZEDES: 'MERCEDES',
  MERCEDESBENZ: 'MERCEDES',
  LANDROVER: 'LAND',
  TOYOTTA: 'TOYOTA',
  HYNDAI: 'HYUNDAI',
  HYUNDAY: 'HYUNDAI',
};

// Vehicle.manufacturer is free text in whatever casing the source used ("VOLKSWAGEN",
// "Škoda", "Mercedes-Benz"). Mirrors normalize_make() in composition/services/vehicle_key.py.
export function normalizeMake(manufacturer) {
  const cleaned = (manufacturer || '')
    .toUpperCase()
    .replace(/Š/g, 'S')
    .replace(/Ž/g, 'Z')
    .replace(/[^A-ZÅÄÖ0-9 ]/g, ' ')
    .trim();
  const make = cleaned ? cleaned.split(/\s+/)[0] : '';
  return MAKE_ALIASES[make] || make;
}

// The manufacturer's service book portal for this make, or null when the make has none that is
// open to independent workshops (or the make is unknown).
export function getServiceBookPortal(manufacturer) {
  const key = MAKE_TO_PORTAL[normalizeMake(manufacturer)];
  return key ? { key, ...SERVICE_BOOK_PORTALS[key] } : null;
}
